import React, { KeyboardEvent, useCallback, useState } from 'react'
import { useTheme } from 'styled-components'
import { Thumbs } from 'swiper'
import { Swiper as SwiperType } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import { Carat, RotateClockwise, useIsBrowser } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { trackEvent } from '../../../../lib/analytics'
import { ProductAsset, Thumbnails } from '../IProps'

import * as Styled from './styles'
// @ts-ignore

const maxThumbnailsVisibleAtATime = 8
const swiperModules = [Thumbs]

export const ThumbNailsDisplay: React.FC<Thumbnails> = ({
  isKit, // TODO: Remove this prop drilling... `useRouter` can tell this component where it is if it cares to know.
  sliderAssets,
  gallerySwiperRef,
  onThumbsSwiperInit,
}) => {
  const {
    pages: {
      product: { gallery: strings },
    },
  } = useTranslation()

  const isBrowser = useIsBrowser()
  const theme = useTheme()
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType>()
  const onThumbsSwiperInitCallback = useCallback(
    (swiper: SwiperType) => {
      setThumbsSwiper(swiper)
      onThumbsSwiperInit?.(swiper)
    },
    [onThumbsSwiperInit]
  )

  const getThumbnail = (slide: ProductAsset) => {
    switch (slide.type) {
      case 'ProductImage':
        return (
          <Styled.ThumbnailImage
            alt={slide.source}
            src={slide.source}
            widths={{ unit: 'px', sm: 100, md: 100 }}
            aspectRatios={{
              sm: 1,
              md: 1,
            }}
            loading='default'
          />
        )
      case 'ProductVideo':
        return (
          <Styled.ThumbnailVideo
            loading='defer'
            autoPlay
            playsInline
            muted
            loop
            src={slide.source}
            type={'video/mp4'}
          />
        )
      default:
        return null
    }
  }

  const shouldDisplayPagination = sliderAssets.length > maxThumbnailsVisibleAtATime

  const handleSlideBackwardClick = () => {
    thumbsSwiper?.slidePrev()
  }

  const handleSlideForwardClick = () => {
    thumbsSwiper?.slideNext()
  }

  const handleThumbnailKeyPress = (event: KeyboardEvent, index: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      gallerySwiperRef?.current?.slideTo(index)
    }
  }

  return (
    <Styled.ThumbnailSwiperWrapper>
      {shouldDisplayPagination && (
        <Styled.ThumbnailSwiperNavButton onClick={handleSlideBackwardClick} $alignItems='start'>
          <Carat
            rotateClockwise={RotateClockwise.ThreeQuarters}
            stroke={theme.color.deprecated.obsidianLighter}
          />
        </Styled.ThumbnailSwiperNavButton>
      )}

      <Swiper
        className='product-images-slider-thumbs'
        direction='vertical'
        modules={swiperModules}
        onInit={onThumbsSwiperInitCallback}
        slidesPerView={maxThumbnailsVisibleAtATime}
      >
        {sliderAssets.map((slide, index: number) => {
          if (!isBrowser && index >= maxThumbnailsVisibleAtATime) return null
          return (
            <SwiperSlide
              key={`slide-${slide.source}-${index}`}
              aria-label={`${strings.viewImage} ${index}`}
              tabIndex={0}
              role='button'
              onKeyDown={event => handleThumbnailKeyPress(event, index)}
              {...trackEvent({
                category: isKit ? 'kits' : 'gallery',
                action: 'click product image thumbnail',
                label: `thumbnail-${index + 1}`,
              })}
            >
              {getThumbnail(slide)}
            </SwiperSlide>
          )
        })}
      </Swiper>
      {shouldDisplayPagination && (
        <Styled.ThumbnailSwiperNavButton onClick={handleSlideForwardClick} $alignItems='end'>
          <Carat
            rotateClockwise={RotateClockwise.Quarter}
            stroke={theme.color.deprecated.obsidianLighter}
          />
        </Styled.ThumbnailSwiperNavButton>
      )}
    </Styled.ThumbnailSwiperWrapper>
  )
}
